import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Tooltip from '../../components/ui/Tooltip';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const PoolTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PoolSubtitle = styled.p`
  margin: 0 0 16px;
  color: #6b7280;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    font-size: 13px;
    color: #6b7280;
  }
  tr:last-child td { border-bottom: none; }
`;

const RowForm = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-top: 12px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  background: ${({ $variant, theme }) =>
    $variant === 'danger' ? '#FEE2E2' : `${theme.colors.primary.main}14`};
  color: ${({ $variant, theme }) =>
    $variant === 'danger' ? '#991B1B' : theme.colors.primary.main};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) =>
      $variant === 'danger' ? '#FCA5A5' : `${theme.colors.primary.main}28`};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export default function CafeteriaPoolDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { canWrite } = useAuth();

  const [pool, setPool] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, itemId: null });
  const [newItem, setNewItem] = useState({ benefitId: '', pointCost: '', maxAllocationPerEmployee: '', sortOrder: 0 });

  const fetchPool = useCallback(async () => {
    try {
      const res = await api.get(`/v1/cafeteria-pools/${id}`);
      setPool(res.data?.data);
    } catch {
      history.push('/cafeteria-pools');
    }
  }, [id, history]);

  useEffect(() => {
    fetchPool();
    api.get('/v1/benefits?isCafeteriaEligible[eq]=true&pageSize=1000').then((res) => {
      setBenefits(res.data?.data?.data || []);
    }).catch(() => {});
  }, [fetchPool]);

  const persist = useCallback(async (items) => {
    if (!pool) return false;
    setSaving(true);
    try {
      await api.put(`/v1/cafeteria-pools/${id}`, {
        name: pool.name,
        description: pool.description,
        openEnrollmentPeriodId: pool.openEnrollmentPeriodId,
        items,
      });
      await fetchPool();
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, pool, fetchPool]);

  const handleAddItem = useCallback(async () => {
    if (!newItem.benefitId) return;
    const items = [
      ...(pool?.items || []).map((it) => ({
        benefitId: it.benefitId,
        pointCost: it.pointCost,
        maxAllocationPerEmployee: it.maxAllocationPerEmployee,
        sortOrder: it.sortOrder ?? 0,
      })),
      {
        benefitId: newItem.benefitId,
        pointCost: newItem.pointCost ? Number(newItem.pointCost) : null,
        maxAllocationPerEmployee: newItem.maxAllocationPerEmployee ? Number(newItem.maxAllocationPerEmployee) : null,
        sortOrder: Number(newItem.sortOrder) || 0,
      },
    ];
    const ok = await persist(items);
    if (ok) setNewItem({ benefitId: '', pointCost: '', maxAllocationPerEmployee: '', sortOrder: 0 });
  }, [newItem, pool, persist]);

  const handleRemoveItem = useCallback(async () => {
    if (!removeConfirm.itemId) return;
    const items = (pool?.items || [])
      .filter((it) => it.id !== removeConfirm.itemId)
      .map((it) => ({
        benefitId: it.benefitId,
        pointCost: it.pointCost,
        maxAllocationPerEmployee: it.maxAllocationPerEmployee,
        sortOrder: it.sortOrder ?? 0,
      }));
    await persist(items);
    setRemoveConfirm({ isOpen: false, itemId: null });
  }, [pool, persist, removeConfirm]);

  const availableBenefits = useMemo(() => {
    const usedIds = new Set((pool?.items || []).map((it) => it.benefitId));
    return benefits.filter((b) => !usedIds.has(b.id));
  }, [benefits, pool]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('cafeteria.poolTitle'), path: '/cafeteria-pools' },
          { label: pool?.name || '...' },
        ]} />
      </StickyHeader>

      <Content>
        {pool && (
          <>
            <Card>
              <PoolTitle>{pool.name}</PoolTitle>
              {pool.description && <PoolSubtitle>{pool.description}</PoolSubtitle>}
              <InfoGrid>
                <div>
                  <InfoLabel>{t('cafeteria.period')}</InfoLabel>
                  <InfoValue>{pool.openEnrollmentPeriodName || t('cafeteria.template')}</InfoValue>
                </div>
                <div>
                  <InfoLabel>{t('cafeteria.itemCount')}</InfoLabel>
                  <InfoValue>{pool.itemCount ?? 0}</InfoValue>
                </div>
              </InfoGrid>
            </Card>

            <Card>
              <SectionTitle>{t('cafeteria.itemsTitle')}</SectionTitle>

              <Table>
                <thead>
                  <tr>
                    <th>{t('cafeteria.itemBenefit')}</th>
                    <th>{t('cafeteria.itemCategory')}</th>
                    <th>{t('cafeteria.itemPointCost')}</th>
                    <th>{t('cafeteria.itemMaxAllocation')}</th>
                    <th>{t('cafeteria.itemSortOrder')}</th>
                    {canWrite('BN') && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {(pool.items || []).length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>{t('cafeteria.noItems')}</td></tr>
                  )}
                  {(pool.items || []).map((it) => (
                    <tr key={it.id}>
                      <td>{it.benefitName}</td>
                      <td>{it.categoryName}</td>
                      <td>{Number(it.effectivePointCost ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                      <td>{it.maxAllocationPerEmployee != null ? Number(it.maxAllocationPerEmployee).toLocaleString('tr-TR') : '-'}</td>
                      <td>{it.sortOrder}</td>
                      {canWrite('BN') && (
                        <td style={{ textAlign: 'right' }}>
                          <Tooltip text={t('common.remove')} position="left">
                            <IconButton
                              $variant="danger"
                              onClick={() => setRemoveConfirm({ isOpen: true, itemId: it.id })}
                              disabled={saving}
                              aria-label={t('common.remove')}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>

              {canWrite('BN') && availableBenefits.length > 0 && (
                <RowForm>
                  <GenericSelect
                    label={t('cafeteria.itemBenefit')}
                    value={newItem.benefitId}
                    onChange={(v) => setNewItem({ ...newItem, benefitId: v })}
                    options={availableBenefits}
                    idKey="id"
                    labelKey="name"
                  />
                  <TextInput
                    type="number"
                    label={t('cafeteria.itemPointCost')}
                    value={newItem.pointCost}
                    onChange={(e) => setNewItem({ ...newItem, pointCost: e.target.value })}
                  />
                  <TextInput
                    type="number"
                    label={t('cafeteria.itemMaxAllocation')}
                    value={newItem.maxAllocationPerEmployee}
                    onChange={(e) => setNewItem({ ...newItem, maxAllocationPerEmployee: e.target.value })}
                  />
                  <TextInput
                    type="number"
                    label={t('cafeteria.itemSortOrder')}
                    value={newItem.sortOrder}
                    onChange={(e) => setNewItem({ ...newItem, sortOrder: e.target.value })}
                  />
                  <Tooltip text={t('common.add')} position="left">
                    <IconButton
                      onClick={handleAddItem}
                      disabled={!newItem.benefitId || saving}
                      aria-label={t('common.add')}>
                      <Plus size={18} />
                    </IconButton>
                  </Tooltip>
                </RowForm>
              )}
            </Card>
          </>
        )}
      </Content>

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title={t('cafeteria.removeItemTitle')}
        message={t('cafeteria.removeItemMessage')}
        onConfirm={handleRemoveItem}
        onCancel={() => setRemoveConfirm({ isOpen: false, itemId: null })}
        loading={saving}
      />
    </PageContainer>
  );
}
